import { types as sdkTypes } from './sdkLoader';

const { LatLng, LatLngBounds } = sdkTypes;
const EARTH_RADIUS_KM = 6371;
const DEG_TO_RAD = Math.PI / 180;
const MAX_MERCATOR_LAT = 85.0511287798066;

// A viewport spanning 30 km on either axis already covers a city and its surroundings.
// This is a geographic threshold, independent of city names and screen dimensions.
export const BROAD_LOCATION_SPAN_KM = 30;
export const SMALL_LOCATION_ZOOM_OUT_LEVELS = 2;

const mercatorY = lat => Math.log(Math.tan(Math.PI / 4 + (lat * DEG_TO_RAD) / 2));
const latitude = y => (2 * Math.atan(Math.exp(y)) - Math.PI / 2) / DEG_TO_RAD;
const wrapLongitude = lng => ((((lng + 180) % 360) + 360) % 360) - 180;
const validPoint = point =>
  point &&
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lng) &&
  Math.abs(point.lat) <= MAX_MERCATOR_LAT &&
  Math.abs(point.lng) <= 180;

/**
 * Apply once to a newly selected search place, before its bounds enter the URL.
 * Never apply to URL bounds, map events, or listing-address inputs.
 *
 * Google supplies a viewport, not a zoom. Scaling both Web Mercator spans by
 * 2^2 lets the existing fitBounds call zoom out two levels at any map size.
 * The very same bounds also reach the listing query, including when the map is hidden.
 */
export const expandSmallLocationSearchPlace = place => {
  const { bounds, origin } = place || {};
  const { ne, sw } = bounds || {};
  if (!validPoint(ne) || !validPoint(sw) || ne.lat <= sw.lat) {
    return place;
  }

  const longitudeSpan = ne.lng >= sw.lng ? ne.lng - sw.lng : ne.lng - sw.lng + 360;
  if (longitudeSpan <= 0) return place;

  const middleLatitude = (ne.lat + sw.lat) / 2;
  const heightKm = EARTH_RADIUS_KM * (ne.lat - sw.lat) * DEG_TO_RAD;
  const widthKm =
    EARTH_RADIUS_KM * longitudeSpan * DEG_TO_RAD * Math.cos(middleLatitude * DEG_TO_RAD);
  if (Math.max(widthKm, heightKm) >= BROAD_LOCATION_SPAN_KM) return place;

  // Saved default suggestions may only have bounds. Keep their existing viewport center.
  const center =
    origin ||
    new LatLng(
      latitude((mercatorY(ne.lat) + mercatorY(sw.lat)) / 2),
      wrapLongitude(sw.lng + longitudeSpan / 2)
    );
  if (!validPoint(center)) return place;

  const scale = 2 ** SMALL_LOCATION_ZOOM_OUT_LEVELS;
  const halfWidth = Math.min(180, (longitudeSpan * scale) / 2);
  const halfHeight = ((mercatorY(ne.lat) - mercatorY(sw.lat)) * scale) / 2;
  const centerY = mercatorY(center.lat);
  const expandedBounds = new LatLngBounds(
    new LatLng(
      latitude(Math.min(Math.PI, centerY + halfHeight)),
      halfWidth === 180 ? 180 : wrapLongitude(center.lng + halfWidth)
    ),
    new LatLng(
      latitude(Math.max(-Math.PI, centerY - halfHeight)),
      halfWidth === 180 ? -180 : wrapLongitude(center.lng - halfWidth)
    )
  );

  return { ...place, bounds: expandedBounds };
};
